import {
  AVAILABLE_PLANS_SECTION_ELEMENT_ID,
  BILLING_MANAGE_SECTION_ELEMENT_ID,
} from '../constants/billingProfilePaths';

const SCROLL_INTO_VIEW_RETRY_MS = 50;
const SCROLL_INTO_VIEW_MAX_ATTEMPTS = 40;
const BILLING_MANAGE_CTA_SELECTOR = '[data-billing-manage-cta]:not([disabled])';
const BILLING_PAGE_HEADING_SELECTOR = '[data-billing-page-heading]';

interface ScrollIntoViewOptions {
  onScrolled?: () => void;
  onNotFound?: () => void;
}

function focusPageHeadingFallback(): void {
  const heading = document.querySelector<HTMLElement>(BILLING_PAGE_HEADING_SELECTOR);
  if (!heading) {
    return;
  }

  if (!heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1');
  }

  heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
  heading.focus({ preventScroll: true });
}

/** Scrolls the primary billing management section into view (retries while billing skeletons mount). */
export function scrollBillingManageIntoView(): void {
  const tryScroll = (attempt: number): void => {
    const el = document.getElementById(BILLING_MANAGE_SECTION_ELEMENT_ID);
    if (!el) {
      if (attempt < SCROLL_INTO_VIEW_MAX_ATTEMPTS) {
        window.setTimeout(() => tryScroll(attempt + 1), SCROLL_INTO_VIEW_RETRY_MS);
        return;
      }

      focusPageHeadingFallback();
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const manageButton = el.querySelector<HTMLElement>(BILLING_MANAGE_CTA_SELECTOR);
    manageButton?.focus({ preventScroll: true });
  };

  tryScroll(0);
}

/** Scrolls the available plans section into view after same-page or hash navigation. */
export function scrollAvailablePlansIntoView(options: ScrollIntoViewOptions = {}): void {
  const tryScroll = (attempt: number): void => {
    const el = document.getElementById(AVAILABLE_PLANS_SECTION_ELEMENT_ID);
    if (!el) {
      if (attempt < SCROLL_INTO_VIEW_MAX_ATTEMPTS) {
        window.setTimeout(() => tryScroll(attempt + 1), SCROLL_INTO_VIEW_RETRY_MS);
        return;
      }
      options.onNotFound?.();
      return;
    }

    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '-1');
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
    options.onScrolled?.();
  };

  tryScroll(0);
}
